import { useQuery, useMutation, useTransport, createConnectQueryKey } from '@connectrpc/connect-query'
import { useQueryClient } from '@tanstack/react-query'
import { create } from '@bufbuild/protobuf'
import { useEffect, useRef } from 'react'

import {
  listSupportThreads,
  getSupportThread,
  getUnreadSupportThreadCount,
  archiveSupportThread,
  sendSupportMessage,
  createSupportThread,
  markSupportThreadRead,
} from '@/gen/cortado/v1/support-SupportService_connectquery'
import {
  ListSupportThreadsRequestSchema,
  GetSupportThreadRequestSchema,
  GetUnreadSupportThreadCountRequestSchema,
  ArchiveSupportThreadRequestSchema,
  SendSupportMessageRequestSchema,
  MarkSupportThreadReadRequestSchema,
} from '@/gen/cortado/v1/support_pb'
import type { ListSupportThreadsResponse } from '@/gen/cortado/v1/support_pb'
import { createSupportClientContext } from '@/lib/support-context'

/**
 * List query for support threads with client-side filtering and sorting.
 */
export function useSupportThreads(orgId: string, statusFilter: string, enabled: boolean) {
  const query = useQuery(
    listSupportThreads,
    create(ListSupportThreadsRequestSchema, {
      organizationId: orgId,
      includeArchived: statusFilter === 'archived',
    }),
    {
      enabled: !!orgId && enabled,
      refetchInterval: enabled ? 30000 : false,
    },
  )

  const threads = (query.data?.threads || [])
    .filter((thread) => {
      if (statusFilter === 'all' || statusFilter === 'archived') return true
      return thread.status === Number(statusFilter)
    })
    .sort((a, b) => a.status - b.status)

  return {
    threads,
    isLoading: query.isLoading,
    hasLoaded: query.isSuccess || !!query.data,
  }
}

/**
 * Detail query for a single thread with its messages.
 * Auto marks thread as read on mount and when new messages arrive.
 */
export function useSupportThread(threadId: string) {
  const queryClient = useQueryClient()
  const transport = useTransport()

  const query = useQuery(
    getSupportThread,
    create(GetSupportThreadRequestSchema, { threadId }),
    {
      enabled: !!threadId,
      refetchInterval: 15000,
    },
  )

  const thread = query.data?.thread
  const messages = query.data?.messages || []
  const hasLoaded = query.isSuccess || !!query.data
  const isArchived = !!thread?.archivedAt

  const { mutate: markRead } = useMutation(markSupportThreadRead, {
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: createConnectQueryKey({
          schema: getUnreadSupportThreadCount,
          transport,
          cardinality: undefined,
        }),
      })
      queryClient.invalidateQueries({
        queryKey: createConnectQueryKey({
          schema: listSupportThreads,
          transport,
          cardinality: undefined,
        }),
      })
    },
  })

  // Mark thread as read on mount / thread switch
  const prevThreadIdRef = useRef<string | null>(null)
  useEffect(() => {
    if (threadId && threadId !== prevThreadIdRef.current) {
      markRead(create(MarkSupportThreadReadRequestSchema, { threadId }))
      prevThreadIdRef.current = threadId
    }
  }, [threadId, markRead])

  // Mark as read when new messages arrive while thread is open
  const prevMessagesLengthRef = useRef(0)
  useEffect(() => {
    if (messages.length > prevMessagesLengthRef.current && prevMessagesLengthRef.current > 0) {
      markRead(create(MarkSupportThreadReadRequestSchema, { threadId }))
    }
    prevMessagesLengthRef.current = messages.length
  }, [messages.length, threadId, markRead])

  return {
    thread,
    messages,
    isLoading: query.isLoading,
    hasLoaded,
    isArchived,
  }
}

/**
 * Unread badge count for support threads.
 */
export function useUnreadSupportCount(orgId: string, enabled: boolean) {
  const query = useQuery(
    getUnreadSupportThreadCount,
    create(GetUnreadSupportThreadCountRequestSchema, { organizationId: orgId }),
    {
      enabled: !!orgId && enabled,
      refetchInterval: 60000,
    },
  )

  return {
    count: query.data?.count ?? 0,
  }
}

/**
 * Archive mutation with optimistic list removal.
 */
export function useSupportArchive() {
  const queryClient = useQueryClient()
  const transport = useTransport()

  const listKey = createConnectQueryKey({
    schema: listSupportThreads,
    transport,
    cardinality: undefined,
  })

  const mutation = useMutation(archiveSupportThread, {
    onMutate: async (variables) => {
      // Cancel in-flight list queries
      await queryClient.cancelQueries({ queryKey: listKey })

      // Snapshot all list caches for rollback
      const previousLists = queryClient.getQueriesData<ListSupportThreadsResponse>({
        queryKey: listKey,
      })

      // Optimistically remove the thread from all list caches
      for (const [key, data] of previousLists) {
        if (!data?.threads) continue
        queryClient.setQueryData(key, {
          ...data,
          threads: data.threads.filter((t) => t.id !== variables.threadId),
        })
      }

      return { previousLists }
    },

    onError: (_error, _variables, context) => {
      // Rollback all list caches
      if (context?.previousLists) {
        for (const [key, data] of context.previousLists) {
          queryClient.setQueryData(key, data)
        }
      }
    },

    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: listKey })
      queryClient.invalidateQueries({
        queryKey: createConnectQueryKey({
          schema: getUnreadSupportThreadCount,
          transport,
          cardinality: undefined,
        }),
      })
    },
  })

  return {
    archive: (threadId: string) =>
      mutation.mutateAsync(create(ArchiveSupportThreadRequestSchema, { threadId })),
    isPending: mutation.isPending,
    archivingThreadId: mutation.variables?.threadId,
  }
}

/**
 * Send message mutation for a support thread.
 */
export function useSupportSendMessage(threadId: string) {
  const queryClient = useQueryClient()
  const transport = useTransport()

  const mutation = useMutation(sendSupportMessage, {
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: createConnectQueryKey({
          schema: getSupportThread,
          transport,
          cardinality: undefined,
        }),
      })
      queryClient.invalidateQueries({
        queryKey: createConnectQueryKey({
          schema: listSupportThreads,
          transport,
          cardinality: undefined,
        }),
      })
    },
  })

  return {
    // Client context is attached here rather than by callers so every send
    // carries the page the user was on, whichever component triggered it.
    send: (body: string) =>
      mutation.mutate(
        create(SendSupportMessageRequestSchema, {
          threadId,
          body,
          clientContext: createSupportClientContext(),
        }),
      ),
    isPending: mutation.isPending,
  }
}

/**
 * Create thread mutation.
 */
export function useSupportCreateThread(onThreadCreated?: (threadId: string) => void) {
  const queryClient = useQueryClient()
  const transport = useTransport()

  const mutation = useMutation(createSupportThread, {
    onSuccess: (data) => {
      queryClient.invalidateQueries({
        queryKey: createConnectQueryKey({
          schema: listSupportThreads,
          transport,
          cardinality: undefined,
        }),
      })
      if (data.thread?.id) {
        onThreadCreated?.(data.thread.id)
      }
    },
  })

  return {
    mutation,
    isPending: mutation.isPending,
  }
}
